export type TileState = 'empty' | 'typed' | 'absent' | 'present' | 'correct';
export type KeyState = 'untouched' | 'absent' | 'present' | 'correct';

export interface CodeWordLevel {
  targetWord?: string;
  answer?: string;
  wordLength?: number;
  maxGuesses?: number;
  validWords?: string[];
  difficulty?: string;
  theme?: string;
}

export interface NormalizedPuzzle {
  targetWord: string;
  wordLength: number;
  maxGuesses: number;
  validWords: Set<string>;
  theme?: string;
}

export interface LetterFeedback {
  letter: string;
  state: TileState;
}

export interface Guess {
  word: string;
  feedback: LetterFeedback[];
}

export function normalizePuzzle(level: CodeWordLevel): NormalizedPuzzle | null {
  const target = (level.targetWord ?? level.answer ?? '').toUpperCase();
  if (!target) return null;
  const wordLength = level.wordLength ?? target.length;
  if (wordLength !== 6 && wordLength !== 7) return null;
  if (target.length !== wordLength) return null;
  const maxGuesses = level.maxGuesses ?? wordLength;
  const validWords = new Set((level.validWords ?? []).map((w) => w.toUpperCase()));
  return { targetWord: target, wordLength, maxGuesses, validWords, theme: level.theme };
}

const PRIORITY: Record<KeyState, number> = {
  untouched: 0,
  absent: 1,
  present: 2,
  correct: 3,
};

export function mergeKeyState(a: KeyState, b: KeyState): KeyState {
  return PRIORITY[a] >= PRIORITY[b] ? a : b;
}

/** Two-pass Wordle evaluator: greens first, then yellows from remaining slots. */
export function evaluateGuess(guess: string, answer: string): LetterFeedback[] {
  const g = guess.split('');
  const a = answer.split('');
  if (g.length !== a.length) {
    return g.map((letter) => ({ letter, state: 'absent' as TileState }));
  }
  const out: TileState[] = Array(g.length).fill('absent');
  const remaining: (string | null)[] = a.slice();
  for (let i = 0; i < g.length; i++) {
    if (g[i] === remaining[i]) {
      out[i] = 'correct';
      remaining[i] = null;
    }
  }
  for (let i = 0; i < g.length; i++) {
    if (out[i] === 'correct') continue;
    const idx = remaining.indexOf(g[i]);
    if (idx >= 0) {
      out[i] = 'present';
      remaining[idx] = null;
    }
  }
  return g.map((letter, i) => ({ letter, state: out[i] }));
}
